import { ConnectError, Code } from '@connectrpc/connect'
import type { Interceptor } from '@connectrpc/connect'
import * as Sentry from '@sentry/browser'
import { sensitiveCallKey, sentryEnabledKey, sourceKey } from './sensitive-call'

/**
 * Safely serialize any value to a JSON-compatible object.
 * Protobuf messages at runtime are plain JavaScript objects that can be serialized.
 * Uses a replacer function to ensure all properties are properly serialized,
 * including those that might be non-enumerable or have special serialization needs.
 */
function serializeValue(value: unknown, fallback = '[unable to serialize]'): unknown {
  try {
    // Use a replacer to handle special cases and ensure all properties are included
    const serialized = JSON.stringify(value, (key, val) => {
      // Handle undefined values (protobuf messages might have undefined fields)
      if (val === undefined) {
        return null
      }
      // Handle non-finite numbers
      if (typeof val === 'number' && (Number.isNaN(val) || !Number.isFinite(val))) {
        return null
      }
      return val
    })
    return JSON.parse(serialized)
  } catch {
    return fallback
  }
}

/**
 * Safely serialize the request message to a JSON-compatible object.
 * Protobuf messages at runtime are plain JavaScript objects that can be serialized.
 */
function serializeRequestMessage(message: unknown, isSensitive: boolean): unknown {
  if (isSensitive) {
    return '[REDACTED - sensitive call]'
  }
  return serializeValue(message, '[unable to serialize request]')
}

/**
 * Extract stable fingerprint parts from an `InvalidArgument` ConnectError's
 * details.
 *
 * Backends paired with `Code.InvalidArgument` typically attach a
 * `google.rpc.BadRequest` protobuf detail with a `debug` JSON payload of the
 * shape:
 *   { fieldViolations: [{ field: "url", description: "url must be provided" }] }
 *
 * `InvalidArgument` errors share a generic `request_is_invalid` rawMessage on
 * the wire, which means every validation failure (missing URL, prompt too
 * long, unsupported tool, etc.) collapses into one bloated Sentry issue.
 * Adding the field + description to the fingerprint splits them into
 * actionable issues.
 *
 * Scoped to `InvalidArgument` deliberately: other codes (`Internal`,
 * `FailedPrecondition`, `Unavailable`, …) already carry meaningful
 * `rawMessage`s, and their detail payloads can contain dynamic content that
 * would explode fingerprint cardinality.
 *
 * Returns a sorted, deduplicated list of `field:description` strings so that
 * the order of violations on the wire doesn't affect grouping.
 */
function extractFieldViolationFingerprintParts(err: ConnectError): string[] {
  if (err.code !== Code.InvalidArgument) {
    return []
  }

  if (!err.details || err.details.length === 0) {
    return []
  }

  const parts = new Set<string>()

  for (const detail of err.details) {
    if (!detail || typeof detail !== 'object') {
      continue
    }

    // Incoming details have a `debug` JSON value. Outgoing details don't,
    // so they're safely skipped by the type guard below.
    const debug = (detail as { debug?: unknown }).debug
    if (!debug || typeof debug !== 'object' || Array.isArray(debug)) {
      continue
    }

    const violations = (debug as { fieldViolations?: unknown }).fieldViolations
    if (!Array.isArray(violations)) {
      continue
    }

    for (const violation of violations) {
      if (!violation || typeof violation !== 'object') {
        continue
      }
      const field = (violation as { field?: unknown }).field
      const description = (violation as { description?: unknown }).description
      if (typeof field !== 'string' && typeof description !== 'string') {
        continue
      }
      const fieldPart = typeof field === 'string' ? field : ''
      const descriptionPart = typeof description === 'string' ? description : ''
      parts.add(`${fieldPart}:${descriptionPart}`)
    }
  }

  return [...parts].sort()
}

/**
 * Extract detailed error information from a ConnectError.
 * Returns an object with all available error details for debugging.
 */
function extractConnectErrorDetails(err: ConnectError): Record<string, unknown> {
  const errorDetails: Record<string, unknown> = {
    errorCode: err.code,
    errorMessage: err.message,
    connectErrorCode: Code[err.code] ?? 'UNKNOWN',
    rawMessage: err.rawMessage,
  }

  // Extract response metadata (headers)
  if (err.metadata && err.metadata.forEach) {
    const metadata: Record<string, string> = {}
    err.metadata.forEach((value, key) => {
      metadata[key] = value
    })
    if (Object.keys(metadata).length > 0) {
      errorDetails.responseMetadata = metadata
    }
  }

  // these are typed protobuf messages from the backend
  // that can contain additional debugging info like field validation errors
  if (err.details && err.details.length > 0) {
    errorDetails.errorDetails = err.details.map(detail => {
      // details are protobuf messages, try to serialize them
      // Extract type name if available (protobuf messages may have $typeName property)
      let typeName = 'unknown'
      if (detail && typeof detail === 'object' && detail !== null && '$typeName' in detail) {
        const typeNameValue = detail.$typeName
        if (typeof typeNameValue === 'string') {
          typeName = typeNameValue
        }
      }

      let serializedData = serializeValue(detail, '[unable to serialize error detail]')
      try {
        serializedData = JSON.stringify(serializedData)
      } catch {}

      return {
        type: typeName,
        data: serializedData,
      }
    })
  }

  // always nice to include the cause if present
  if (err.cause) {
    errorDetails.cause =
      err.cause instanceof Error
        ? { name: err.cause.name, message: err.cause.message }
        : String(err.cause)
  }

  return errorDetails
}

export const sentryInterceptor: Interceptor = next => async req => {
  try {
    return await next(req)
    // oxlint-disable-next-line unicorn/catch-error-name
  } catch (err) {
    const isSensitive = req.contextValues?.get(sensitiveCallKey) ?? false
    const isSentryEnabled = req.contextValues?.get(sentryEnabledKey) ?? true
    const source = req.contextValues?.get(sourceKey)

    const extra: Record<string, unknown> = {
      rpcUrl: req.url,
      rpcMethod: req.method.name,
      rpcService: req.service.typeName,
      requestPayload: serializeRequestMessage(req.message, isSensitive),
      sentryEnabled: isSentryEnabled,
      ...(source ? { source } : {}),
    }

    if (!isSentryEnabled) {
      throw err
    }

    if (err instanceof ConnectError) {
      Object.assign(extra, extractConnectErrorDetails(err))

      // `Unauthenticated` 401s are largely caused by best-effort client guards
      // racing against the auth token state (e.g. `useBatchLikedFeeds` enables
      // on cached `isLoggedIn` while `getToken()` still holds a guest token, or
      // vice versa). They're recoverable noise rather than actionable errors,
      // so we capture them as warnings — they still show up in Sentry with the
      // full payload, but drop out of error dashboards/alerts. A backend
      // regression that starts 401-ing a different RPC will spin up its own
      // issue thanks to `req.method.name` in the fingerprint below.
      const isUnauthenticated = err.code === Code.Unauthenticated
      // Field-level violations from the error's protobuf details (e.g.
      // google.rpc.BadRequest) so that distinct validation failures don't
      // collapse into one bloated issue. See
      // `extractFieldViolationFingerprintParts` for the shape we expect.
      const fieldViolationParts = extractFieldViolationFingerprintParts(err)

      // uses a custom fingerprint to force Sentry to group by
      // the error code AND the specific message
      Sentry.captureException(err, {
        level: isUnauthenticated ? 'warning' : 'error',
        extra,
        fingerprint: [
          '{{ default }}',
          err.code.toString(),
          err.rawMessage,
          ...(isUnauthenticated ? [req.method.name] : []),
          ...fieldViolationParts,
        ],
      })
    } else {
      Sentry.captureException(err, { extra })
    }

    throw err // gotta rethrow so the error is handled by the caller
  }
}
